import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult } from "@topzle/types";
import { z } from "zod";

const colorSchema = z.enum(["red", "green", "yellow", "blue"]);
export type LudoColor = z.infer<typeof colorSchema>;
export interface LudoBoardCoordinate {
  row: number;
  column: number;
}
const tokenSchema = z.object({
  id: z.number().int().min(0).max(3),
  progress: z.number().int().min(-1).max(57),
});
const playerSchema = z.object({
  id: z.string(),
  color: colorSchema,
  tokens: z.array(tokenSchema).length(4),
  finished: z.number().int().min(0).max(4),
  captures: z.number().int().nonnegative().default(0),
});
const historyEntrySchema = z.object({
  playerId: z.string(),
  dice: z.number().int().min(1).max(6),
  tokenId: z.number().int().min(0).max(3).nullable(),
  automatic: z.boolean().optional(),
  captureCount: z.number().int().min(0).max(12).optional(),
  capturedPlayerIds: z.array(z.string()).max(3).optional(),
  reachedHome: z.boolean().optional(),
  diceMode: z.enum(["fair", "smart"]).optional(),
  diceWeights: z
    .tuple([z.number(), z.number(), z.number(), z.number(), z.number(), z.number()])
    .optional(),
  at: z.number(),
});
const throwStyleSchema = z.object({
  force: z.number().min(0.18).max(1),
  momentumX: z.number().min(-125).max(125),
  momentumY: z.number().min(-125).max(125),
  pathTilt: z.number().min(-20).max(20),
});
const lastThrowSchema = throwStyleSchema.extend({
  playerId: z.string(),
  rollNonce: z.number().int().nonnegative(),
});
export const ludoStateSchema = z.object({
  players: z.array(playerSchema).min(2).max(4),
  turnIndex: z.number().int().min(0).max(3),
  dice: z.number().int().min(1).max(6).nullable(),
  phase: z.enum(["roll", "roll_result", "move", "finished"]),
  turnAvailableAt: z.number().nullable().default(null),
  actionDeadlineAt: z.number().nullable().default(null),
  rollNonce: z.number().int().nonnegative(),
  lastThrow: lastThrowSchema.nullable().default(null),
  winnerIds: z.array(z.string()),
  requireSixToLeave: z.boolean(),
  requireCaptureBeforeHome: z.boolean().default(true),
  extraTurnOnSix: z.boolean(),
  extraTurnOnCapture: z.boolean().default(true),
  extraTurnOnHome: z.boolean().default(true),
  threeSixesForfeit: z.boolean().default(true),
  blockadesEnabled: z.boolean().default(true),
  fastMode: z.boolean().default(false),
  smartDiceEnabled: z.boolean().default(true),
  rollTimeSeconds: z.number().int().min(10).max(120).default(30),
  moveTimeSeconds: z.number().int().min(10).max(120).default(30),
  consecutiveSixes: z.number().int().min(0).max(2).default(0),
  sixChainSnapshot: z
    .object({
      players: z.array(playerSchema).min(2).max(4),
      winnerIds: z.array(z.string()),
      historyLength: z.number().int().min(0).max(500),
    })
    .nullable()
    .default(null),
  history: z.array(historyEntrySchema).max(505),
});
export const ludoEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("roll"), throwStyle: throwStyleSchema.optional() }),
  z.object({ type: z.literal("move"), tokenId: z.number().int().min(0).max(3) }),
  z.object({ type: z.literal("set_fast_mode"), enabled: z.boolean() }),
]);
export const ludoSettingsSchema = z.object({
  requireSixToLeave: z.boolean().default(true),
  requireCaptureBeforeHome: z.boolean().default(true),
  extraTurnOnSix: z.boolean().default(true),
  extraTurnOnCapture: z.boolean().default(true),
  extraTurnOnHome: z.boolean().default(true),
  threeSixesForfeit: z.boolean().default(true),
  blockadesEnabled: z.boolean().default(true),
  fastMode: z.boolean().default(false),
  rollTimeSeconds: z.number().int().min(10).max(120).default(30),
  moveTimeSeconds: z.number().int().min(10).max(120).default(30),
});
export type LudoState = z.infer<typeof ludoStateSchema>;
export type LudoEvent = z.infer<typeof ludoEventSchema>;
export type LudoSettings = z.infer<typeof ludoSettingsSchema>;

export interface LudoDiceProfile {
  mode: "fair" | "smart";
  weights: [number, number, number, number, number, number];
  probabilities: [number, number, number, number, number, number];
  reasons: string[];
}

export const LUDO_ROLL_RESULT_MS = 3_000;
export const LUDO_FAST_ROLL_RESULT_MS = 850;
export const LUDO_DICE_PRESENTATION_MS = 2_200;
export const LUDO_FAST_DICE_PRESENTATION_MS = 850;

export const ludoColors = ["red", "green", "yellow", "blue"] as const;
export const ludoStarts: Record<LudoColor, number> = {
  red: 0,
  green: 13,
  yellow: 26,
  blue: 39,
};
export const ludoSafeSquares = new Set([0, 8, 13, 21, 26, 34, 39, 47]);
export const ludoBoardPath: LudoBoardCoordinate[] = [
  ...[1, 2, 3, 4, 5].map((column) => ({ row: 6, column })),
  ...[5, 4, 3, 2, 1, 0].map((row) => ({ row, column: 6 })),
  { row: 0, column: 7 },
  { row: 0, column: 8 },
  ...[1, 2, 3, 4, 5].map((row) => ({ row, column: 8 })),
  ...[9, 10, 11, 12, 13, 14].map((column) => ({ row: 6, column })),
  { row: 7, column: 14 },
  { row: 8, column: 14 },
  ...[13, 12, 11, 10, 9].map((column) => ({ row: 8, column })),
  ...[9, 10, 11, 12, 13, 14].map((row) => ({ row, column: 8 })),
  { row: 14, column: 7 },
  { row: 14, column: 6 },
  ...[13, 12, 11, 10, 9].map((row) => ({ row, column: 6 })),
  ...[5, 4, 3, 2, 1, 0].map((column) => ({ row: 8, column })),
  { row: 7, column: 0 },
  { row: 6, column: 0 },
];
export const ludoHomeLanes: Record<LudoColor, LudoBoardCoordinate[]> = {
  red: [1, 2, 3, 4, 5].map((column) => ({ row: 7, column })),
  green: [1, 2, 3, 4, 5].map((row) => ({ row, column: 7 })),
  yellow: [13, 12, 11, 10, 9].map((column) => ({ row: 7, column })),
  blue: [13, 12, 11, 10, 9].map((row) => ({ row, column: 7 })),
};
export const ludoGoals: Record<LudoColor, LudoBoardCoordinate> = {
  red: { row: 7, column: 6 },
  green: { row: 6, column: 7 },
  yellow: { row: 7, column: 8 },
  blue: { row: 8, column: 7 },
};

export function ludoGlobalSquare(color: LudoColor, progress: number): number | null {
  if (progress < 0 || progress > 50) return null;
  return (ludoStarts[color] + progress) % 52;
}

export function ludoTokenCoordinate(
  color: LudoColor,
  progress: number,
): LudoBoardCoordinate | null {
  const sharedSquare = ludoGlobalSquare(color, progress);
  if (sharedSquare !== null) return ludoBoardPath[sharedSquare] ?? null;
  if (progress >= 51 && progress <= 55) return ludoHomeLanes[color][progress - 51] ?? null;
  if (progress >= 56) return ludoGoals[color];
  return null;
}

function findTwoPlayerLockedWinner(state: Pick<LudoState, "players" | "requireCaptureBeforeHome">) {
  if (!state.requireCaptureBeforeHome || state.players.length !== 2) return null;

  for (const candidate of state.players) {
    const opponent = state.players.find((player) => player.id !== candidate.id);
    const allGotisPermanentlySafe = candidate.tokens.every((token) => token.progress >= 51);
    if (candidate.captures > 0 && allGotisPermanentlySafe && opponent?.captures === 0) {
      return candidate.id;
    }
  }

  return null;
}

function opponentBlockades(state: LudoState, playerId: string): Set<number> {
  const counts = new Map<string, number>();
  for (const opponent of state.players) {
    if (opponent.id === playerId) continue;
    for (const token of opponent.tokens) {
      const square = ludoGlobalSquare(opponent.color, token.progress);
      if (square === null || ludoSafeSquares.has(square)) continue;
      const key = `${opponent.id}:${square}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return new Set(
    [...counts.entries()]
      .filter(([, count]) => count >= 2)
      .map(([key]) => Number(key.split(":").at(-1))),
  );
}

export type LudoMoveBlockReason =
  | "no_roll"
  | "finished"
  | "needs_six"
  | "overshoot"
  | "capture_required"
  | "blockade";

export interface LudoMoveAnalysis {
  legal: boolean;
  reason: LudoMoveBlockReason | null;
  destinationProgress: number | null;
  captureCount: number;
}

export function analyzeLudoMove(
  state: LudoState,
  player: LudoState["players"][number],
  token: LudoState["players"][number]["tokens"][number],
  dice = state.dice ?? 0,
): LudoMoveAnalysis {
  if (dice < 1)
    return { legal: false, reason: "no_roll", destinationProgress: null, captureCount: 0 };
  if (token.progress >= 56)
    return { legal: false, reason: "finished", destinationProgress: null, captureCount: 0 };
  if (token.progress === -1 && state.requireSixToLeave && dice !== 6)
    return { legal: false, reason: "needs_six", destinationProgress: null, captureCount: 0 };

  const destinationProgress = token.progress === -1 ? 0 : token.progress + dice;
  if (destinationProgress > 56)
    return { legal: false, reason: "overshoot", destinationProgress, captureCount: 0 };
  if (
    state.requireCaptureBeforeHome &&
    player.captures === 0 &&
    token.progress <= 50 &&
    destinationProgress >= 51
  )
    return {
      legal: false,
      reason: "capture_required",
      destinationProgress,
      captureCount: 0,
    };

  if (state.blockadesEnabled && token.progress >= 0) {
    const blockades = opponentBlockades(state, player.id);
    for (
      let progress = token.progress + 1;
      progress <= Math.min(destinationProgress, 50);
      progress += 1
    ) {
      const square = ludoGlobalSquare(player.color, progress);
      if (square !== null && blockades.has(square))
        return { legal: false, reason: "blockade", destinationProgress, captureCount: 0 };
    }
  }

  const destinationSquare = ludoGlobalSquare(player.color, destinationProgress);
  let captureCount = 0;
  if (destinationSquare !== null && !ludoSafeSquares.has(destinationSquare)) {
    for (const opponent of state.players) {
      if (opponent.id === player.id) continue;
      for (const opponentToken of opponent.tokens) {
        if (ludoGlobalSquare(opponent.color, opponentToken.progress) === destinationSquare)
          captureCount += 1;
      }
    }
  }
  return { legal: true, reason: null, destinationProgress, captureCount };
}

export function canMoveLudoToken(
  state: LudoState,
  player: LudoState["players"][number],
  token: LudoState["players"][number]["tokens"][number],
  dice = state.dice ?? 0,
): boolean {
  return analyzeLudoMove(state, player, token, dice).legal;
}

function nextTurn(state: LudoState): number {
  for (let offset = 1; offset <= state.players.length; offset += 1) {
    const candidate = (state.turnIndex + offset) % state.players.length;
    if ((state.players[candidate]?.finished ?? 4) < 4) return candidate;
  }
  return state.turnIndex;
}

function clonePlayers(players: LudoState["players"]): LudoState["players"] {
  return players.map((player) => ({
    ...player,
    tokens: player.tokens.map((token) => ({ ...token })),
  }));
}

function markLatestLudoActionAutomatic(state: LudoState): LudoState {
  const latest = state.history.at(-1);
  if (!latest) return state;
  return {
    ...state,
    history: [...state.history.slice(0, -1), { ...latest, automatic: true }],
  };
}

interface LudoBotMoveAnalysis {
  captures: number;
  capturedProgress: number;
  createsBlockade: boolean;
  destinationProgress: number;
  destinationSafe: boolean;
  finishes: boolean;
  leavesYard: boolean;
  movesThreatenedToken: boolean;
  risk: number;
}

function captureThreatCount(state: LudoState, playerId: string, square: number | null): number {
  if (square === null || ludoSafeSquares.has(square)) return 0;
  let threats = 0;
  for (const opponent of state.players) {
    if (opponent.id === playerId) continue;
    for (const token of opponent.tokens) {
      const opponentSquare = ludoGlobalSquare(opponent.color, token.progress);
      if (opponentSquare === null) continue;
      const distance = (square - opponentSquare + 52) % 52;
      if (distance >= 1 && distance <= 6 && token.progress + distance <= 50) {
        threats += 7 - distance;
      }
    }
  }
  return threats;
}

export type LudoBotEmotion =
  | "calm"
  | "confident"
  | "desperate"
  | "finishing"
  | "hunting"
  | "nervous"
  | "focused";

export interface LudoBotThrowProfile {
  emotion: LudoBotEmotion;
  label: string;
  reason: string;
  delayMs: number;
  force: number;
  momentumX: number;
  momentumY: number;
  pathTilt: number;
}

function ludoRaceScore(player: LudoState["players"][number]) {
  return (
    player.tokens.reduce(
      (total, token) => total + (token.progress < 0 ? 0 : Math.min(56, token.progress) + 1),
      0,
    ) +
    player.captures * 5
  );
}

function normalizeDiceWeights(
  weights: [number, number, number, number, number, number],
): LudoDiceProfile["probabilities"] {
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  return weights.map((weight) => weight / total) as LudoDiceProfile["probabilities"];
}

/**
 * Returns the disclosed, auditable weighting used by competitive Ludo rooms.
 * Every face keeps a positive chance. Fair mode always returns six equal weights.
 */
export function getLudoDiceProfile(state: LudoState): LudoDiceProfile {
  const fairWeights: LudoDiceProfile["weights"] = [1, 1, 1, 1, 1, 1];
  if (!state.smartDiceEnabled) {
    return {
      mode: "fair",
      weights: fairWeights,
      probabilities: normalizeDiceWeights(fairWeights),
      reasons: ["Smart dice is disabled by the platform control."],
    };
  }

  const player = state.players[state.turnIndex];
  if (!player) {
    return {
      mode: "smart",
      weights: fairWeights,
      probabilities: normalizeDiceWeights(fairWeights),
      reasons: ["No active player was available, so this roll remains uniform."],
    };
  }

  const unfinished = player.tokens.filter((token) => token.progress < 56);
  const allUnfinishedInYard =
    unfinished.length > 0 && unfinished.every((token) => token.progress < 0);
  if (state.requireSixToLeave && allUnfinishedInYard) {
    const yardWeights: LudoDiceProfile["weights"] = [4, 4, 4, 4, 4, 10];
    return {
      mode: "smart",
      weights: yardWeights,
      probabilities: normalizeDiceWeights(yardWeights),
      reasons: ["All unfinished gotis are in the yard, so six has an exact 1 in 3 chance."],
    };
  }

  const scores = state.players.map(ludoRaceScore);
  const playerScore = scores[state.turnIndex] ?? 0;
  const leaderScore = Math.max(...scores);
  const trailerScore = Math.min(...scores);
  const spread = Math.max(56, leaderScore - trailerScore);
  const deficit = Math.max(0, leaderScore - playerScore);
  const lead = Math.max(
    0,
    playerScore - Math.max(0, ...scores.filter((_, index) => index !== state.turnIndex)),
  );
  const competitiveMultiplier = Math.max(
    0.65,
    Math.min(1.5, 1 + (deficit / spread) * 0.5 - (lead / spread) * 0.35),
  );
  const bonuses: [number, number, number, number, number, number] = [0, 0, 0, 0, 0, 0];
  const captureFaces = new Set<number>();
  const finishFaces = new Set<number>();
  const legalFaces = new Set<number>();

  for (let face = 1; face <= 6; face += 1) {
    for (const token of unfinished) {
      const analysis = analyzeLudoMove(state, player, token, face);
      if (!analysis.legal) continue;
      legalFaces.add(face);
      if (analysis.captureCount > 0) captureFaces.add(face);
      if (analysis.destinationProgress === 56) finishFaces.add(face);
    }
  }

  for (const face of legalFaces) bonuses[face - 1] = (bonuses[face - 1] ?? 0) + 1.5;
  for (const face of finishFaces) bonuses[face - 1] = (bonuses[face - 1] ?? 0) + 5;
  for (const face of captureFaces) {
    bonuses[face - 1] = (bonuses[face - 1] ?? 0) + 8;
    if (face > 1) bonuses[face - 2] = (bonuses[face - 2] ?? 0) + 3.5;
    if (face < 6) bonuses[face] = (bonuses[face] ?? 0) + 3.5;
  }
  if (unfinished.some((token) => token.progress < 0) && state.requireSixToLeave)
    bonuses[5] = (bonuses[5] ?? 0) + 2;

  const weights = bonuses.map((bonus) =>
    Math.max(10, Math.round(10 + bonus * competitiveMultiplier)),
  ) as LudoDiceProfile["weights"];
  const reasons: string[] = [];
  if (captureFaces.size > 0)
    reasons.push(`Capture pressure increased faces near ${[...captureFaces].join(", ")}.`);
  if (finishFaces.size > 0) reasons.push("Exact home rolls received a modest boost.");
  if (deficit > 0) reasons.push("The trailing player received a capped catch-up multiplier.");
  if (lead > 0) reasons.push("The leading player's tactical bonus was reduced.");
  if (reasons.length === 0)
    reasons.push("No strong tactical opportunity was found; the roll is close to uniform.");
  return {
    mode: "smart",
    weights,
    probabilities: normalizeDiceWeights(weights),
    reasons,
  };
}

export function rollLudoDice(
  state: LudoState,
  randomInteger: (maximumExclusive: number) => number,
): {
  dice: number;
  profile: LudoDiceProfile;
} {
  const profile = getLudoDiceProfile(state);
  const total = profile.weights.reduce((sum, weight) => sum + weight, 0);
  let selection = randomInteger(total);
  for (let index = 0; index < profile.weights.length; index += 1) {
    selection -= profile.weights[index] ?? 0;
    if (selection < 0) return { dice: index + 1, profile };
  }
  return { dice: 6, profile };
}

export function getLudoBotThrowProfile(
  state: LudoState,
  playerId: string,
  difficulty: BotContext["difficulty"] = "medium",
): LudoBotThrowProfile {
  const player = state.players.find((candidate) => candidate.id === playerId);
  if (!player) {
    return {
      emotion: "calm",
      label: "MEASURED THROW",
      reason: "Reading the board",
      delayMs: 1_350,
      force: 0.55,
      momentumX: 0,
      momentumY: 0,
      pathTilt: 0,
    };
  }

  const opponents = state.players.filter((candidate) => candidate.id !== player.id);
  const playerScore = ludoRaceScore(player);
  const leadingOpponentScore = Math.max(0, ...opponents.map(ludoRaceScore));
  const scoreDeficit = leadingOpponentScore - playerScore;
  const activeTokens = player.tokens.filter((token) => token.progress >= 0 && token.progress < 56);
  const threatenedTokens = activeTokens.filter(
    (token) =>
      captureThreatCount(state, player.id, ludoGlobalSquare(player.color, token.progress)) > 0,
  ).length;
  const huntableTokens = activeTokens.reduce((total, token) => {
    const square = ludoGlobalSquare(player.color, token.progress);
    if (square === null) return total;
    const canHunt = opponents.some((opponent) =>
      opponent.tokens.some((opponentToken) => {
        const targetSquare = ludoGlobalSquare(opponent.color, opponentToken.progress);
        if (targetSquare === null || ludoSafeSquares.has(targetSquare)) return false;
        const distance = (targetSquare - square + 52) % 52;
        return distance >= 1 && distance <= 6 && token.progress + distance <= 50;
      }),
    );
    return total + Number(canHunt);
  }, 0);
  const homeLaneTokens = activeTokens.filter((token) => token.progress >= 51).length;
  const approachingSafety = activeTokens.some(
    (token) => token.progress >= 44 && token.progress <= 50,
  );
  const opponentNearWin = opponents.some(
    (opponent) =>
      opponent.finished >= 3 ||
      opponent.tokens.filter((token) => token.progress >= 51 && token.progress < 56).length >= 2,
  );
  const finishing =
    player.finished >= 3 || (activeTokens.length > 0 && homeLaneTokens === activeTokens.length);
  const comfortablyAhead = playerScore - leadingOpponentScore >= 58 && !opponentNearWin;
  const recentWindowStart = Math.max(0, state.history.length - (state.players.length * 2 + 2));
  let recentMoveIndex = -1;
  let lastBotMoveIndex = -1;
  let lastBotCutIndex = -1;
  let lastOpponentHomeIndex = -1;
  for (let index = recentWindowStart; index < state.history.length; index += 1) {
    const entry = state.history[index];
    if (!entry || entry.tokenId === null) continue;
    recentMoveIndex = index;
    if (entry.playerId === player.id) lastBotMoveIndex = index;
    if (entry.capturedPlayerIds?.includes(player.id)) lastBotCutIndex = index;
    if (entry.playerId !== player.id && entry.reachedHome) lastOpponentHomeIndex = index;
  }
  const recentMove = recentMoveIndex >= 0 ? state.history[recentMoveIndex] : undefined;
  const botWasCut = lastBotCutIndex > lastBotMoveIndex;
  const opponentReachedHome = lastOpponentHomeIndex > lastBotMoveIndex;
  const botMadeCut = recentMove?.playerId === player.id && (recentMove.captureCount ?? 0) > 0;
  const botReachedHome = recentMove?.playerId === player.id && recentMove?.reachedHome === true;

  let emotion: LudoBotEmotion = "calm";
  let label = "MEASURED THROW";
  let reason = "Reading the board";
  let delayMs = 1_350;
  let force = 0.55;

  if (botWasCut) {
    emotion = "desperate";
    label = "GOTI CUT · FIRED UP";
    reason = "Throwing hard after being sent back";
    delayMs = 680;
    force = 1;
  } else if (opponentReachedHome) {
    emotion = "nervous";
    label = "RACE PRESSURE RISING";
    reason = "An opponent just brought a goti home";
    delayMs = 820;
    force = 0.95;
  } else if (botMadeCut) {
    emotion = "hunting";
    label = "MOMENTUM AFTER A CUT";
    reason = "Energized by the successful capture";
    delayMs = 920;
    force = 0.86;
  } else if (botReachedHome) {
    emotion = "confident";
    label = "RELIEVED · SETTLED THROW";
    reason = "A goti is safely home";
    delayMs = 1_480;
    force = 0.47;
  } else if (state.consecutiveSixes === 2) {
    emotion = "focused";
    label = "THIRD-SIX CAUTION";
    reason = "A third six would cancel the whole chain";
    delayMs = 1_900;
    force = 0.5;
  } else if (comfortablyAhead) {
    emotion = "confident";
    label = "CALM LEADER'S THROW";
    reason = "Ahead and in control";
    delayMs = 1_500;
    force = 0.46;
  } else if (finishing) {
    emotion = "finishing";
    label = "FINISH-LINE FOCUS";
    reason = player.finished >= 3 ? "One last goti can win it" : "Waiting for an exact home roll";
    delayMs = 1_850;
    force = 0.78;
  } else if (scoreDeficit >= 72 || (opponentNearWin && player.finished === 0)) {
    emotion = "desperate";
    label = "HIGH-PRESSURE THROW";
    reason = "An opponent is far ahead";
    delayMs = 780;
    force = 1;
  } else if (threatenedTokens >= 2 || (threatenedTokens > 0 && opponentNearWin)) {
    emotion = "nervous";
    label = "DANGER · QUICK THROW";
    reason = `${threatenedTokens} gotis are exposed`;
    delayMs = 900;
    force = 0.93;
  } else if (huntableTokens > 0) {
    emotion = "hunting";
    label = "HUNTING A CUT";
    reason = "An opponent is within striking distance";
    delayMs = 1_050;
    force = 0.9;
  } else if (threatenedTokens > 0 || approachingSafety) {
    emotion = "focused";
    label = threatenedTokens > 0 ? "ESCAPE ROUTE" : "PLAYING FOR SAFETY";
    reason = threatenedTokens > 0 ? "Trying to save an exposed goti" : "A safe zone is close";
    delayMs = 1_600;
    force = 0.68;
  } else if (player.finished >= 2) {
    emotion = "confident";
    label = "STEADY ADVANTAGE";
    reason = "Keeping a measured lead";
    delayMs = 1_420;
    force = 0.52;
  }

  const difficultyDelay = difficulty === "hard" ? 160 : difficulty === "easy" ? -140 : 0;
  const seed = state.rollNonce * 29 + player.captures * 17 + player.finished * 31;
  const swayX = ((seed % 9) - 4) / 4;
  const swayY = (((seed * 5 + 3) % 9) - 4) / 4;
  const momentumScale =
    emotion === "desperate" || emotion === "nervous"
      ? 92
      : emotion === "hunting"
        ? 76
        : emotion === "finishing"
          ? 44
          : 58;

  return {
    emotion,
    label,
    reason,
    delayMs: Math.max(650, delayMs + difficultyDelay),
    force,
    momentumX: swayX * momentumScale,
    momentumY: swayY * momentumScale,
    pathTilt: swayX * (emotion === "nervous" || emotion === "desperate" ? 18 : 11),
  };
}

function analyzeLudoBotMove(
  state: LudoState,
  player: LudoState["players"][number],
  token: LudoState["players"][number]["tokens"][number],
): LudoBotMoveAnalysis {
  const dice = state.dice ?? 0;
  const destinationProgress = token.progress === -1 ? 0 : token.progress + dice;
  const sourceSquare = ludoGlobalSquare(player.color, token.progress);
  const destinationSquare = ludoGlobalSquare(player.color, destinationProgress);
  let captures = 0;
  let capturedProgress = 0;

  if (destinationSquare !== null && !ludoSafeSquares.has(destinationSquare)) {
    for (const opponent of state.players) {
      if (opponent.id === player.id) continue;
      for (const opponentToken of opponent.tokens) {
        if (ludoGlobalSquare(opponent.color, opponentToken.progress) === destinationSquare) {
          captures += 1;
          capturedProgress += Math.max(0, opponentToken.progress);
        }
      }
    }
  }

  const friendlyAtDestination =
    destinationSquare === null
      ? 0
      : player.tokens.filter(
          (entry) =>
            entry.id !== token.id &&
            ludoGlobalSquare(player.color, entry.progress) === destinationSquare,
        ).length;
  const destinationSafe =
    destinationProgress >= 51 ||
    (destinationSquare !== null && ludoSafeSquares.has(destinationSquare));
  const sourceRisk = captureThreatCount(state, player.id, sourceSquare);
  const destinationRisk = captureThreatCount(state, player.id, destinationSquare);

  return {
    captures,
    capturedProgress,
    createsBlockade:
      Boolean(state.blockadesEnabled) &&
      destinationSquare !== null &&
      !destinationSafe &&
      friendlyAtDestination > 0,
    destinationProgress,
    destinationSafe,
    finishes: destinationProgress === 56,
    leavesYard: token.progress === -1,
    movesThreatenedToken: sourceRisk > 0 && destinationRisk < sourceRisk,
    risk: destinationRisk,
  };
}

export function scoreLudoBotMove(
  state: LudoState,
  tokenId: number,
  difficulty: BotContext["difficulty"],
): number {
  const player = state.players[state.turnIndex];
  const token = player?.tokens[tokenId];
  if (!player || !token || state.dice === null || !canMoveLudoToken(state, player, token)) {
    return Number.NEGATIVE_INFINITY;
  }
  const analysis = analyzeLudoBotMove(state, player, token);
  if (difficulty === "easy") {
    return ((state.rollNonce + token.id * 11) % 17) + analysis.destinationProgress / 100;
  }

  const activeTokens = player.tokens.filter(
    (entry) => entry.progress >= 0 && entry.progress < 56,
  ).length;
  const commonScore =
    analysis.destinationProgress * 2 +
    (analysis.finishes ? 1_250 : 0) +
    analysis.captures * 620 +
    analysis.capturedProgress * 3 +
    (analysis.destinationSafe ? 115 : 0) +
    (analysis.movesThreatenedToken ? 170 : 0) +
    (analysis.createsBlockade ? 210 : 0) +
    (analysis.leavesYard ? (activeTokens < 2 ? 175 : 85) : 0) -
    analysis.risk * 52;

  if (difficulty === "medium") return commonScore;

  const homeLaneProgress = Math.max(0, analysis.destinationProgress - 50);
  const spreadBonus =
    analysis.leavesYard && activeTokens < 3 ? 90 : Math.min(analysis.destinationProgress, 40);
  const finishPressure = player.finished >= 2 ? homeLaneProgress * 42 : homeLaneProgress * 24;
  const bonusTurnValue =
    (analysis.captures && state.extraTurnOnCapture) || (analysis.finishes && state.extraTurnOnHome)
      ? 180
      : 0;

  return (
    commonScore +
    spreadBonus +
    finishPressure +
    bonusTurnValue -
    analysis.risk * 31 +
    ((state.rollNonce * 7 + token.id * 3) % 5)
  );
}

export function chooseLudoBotAction(
  state: LudoState,
  difficulty: BotContext["difficulty"],
): LudoEvent {
  if (state.phase === "roll") return { type: "roll" };
  const player = state.players[state.turnIndex];
  if (!player || state.dice === null) throw new Error("Ludo bot cannot move now");
  const movable = player.tokens.filter((token) =>
    canMoveLudoToken(state, player, token, state.dice ?? 0),
  );
  if (!movable.length) throw new Error("Ludo bot has no legal token");
  const scored = movable.map((token) => ({
    token,
    score: scoreLudoBotMove(state, token.id, difficulty),
  }));
  scored.sort((left, right) => right.score - left.score || left.token.id - right.token.id);
  const selected = scored[0];
  return { type: "move", tokenId: selected?.token.id ?? movable[0]?.id ?? 0 };
}

export type LudoResearchStrategy = "aggressive" | "balanced" | "defensive" | "random";

/** Shared policy selector used by both live rooms and the reproducible bot lab. */
export function chooseLudoResearchAction(
  state: LudoState,
  strategy: LudoResearchStrategy,
  randomInteger: (maximumExclusive: number) => number = () => 0,
): LudoEvent {
  if (state.phase === "roll") return { type: "roll" };
  const player = state.players[state.turnIndex];
  if (!player || state.dice === null) throw new Error("Ludo research bot cannot move now");
  const movable = player.tokens.filter((token) => canMoveLudoToken(state, player, token));
  if (movable.length === 0) throw new Error("Ludo research bot has no legal token");
  if (strategy === "random") {
    const selected = movable[randomInteger(movable.length)] ?? movable[0];
    return { type: "move", tokenId: selected?.id ?? 0 };
  }
  if (strategy === "balanced") return chooseLudoBotAction(state, "hard");

  const scored = movable.map((token) => {
    const analysis = analyzeLudoBotMove(state, player, token);
    const score =
      strategy === "aggressive"
        ? analysis.captures * 2_400 +
          analysis.capturedProgress * 6 +
          (analysis.finishes ? 1_100 : 0) +
          analysis.destinationProgress * 3 +
          (analysis.leavesYard ? 90 : 0) -
          analysis.risk * 12
        : (analysis.destinationSafe ? 1_000 : 0) +
          (analysis.movesThreatenedToken ? 1_300 : 0) +
          (analysis.createsBlockade ? 900 : 0) +
          (analysis.finishes ? 1_100 : 0) +
          analysis.destinationProgress * 2 -
          analysis.risk * 190 +
          analysis.captures * 220;
    return { token, score };
  });
  scored.sort((left, right) => right.score - left.score || left.token.id - right.token.id);
  return { type: "move", tokenId: scored[0]?.token.id ?? movable[0]?.id ?? 0 };
}

export const ludoGame: TopzleGameDefinition<LudoState, LudoEvent, LudoSettings> = {
  id: "ludo",
  version: "1.0.0",
  name: "Ludo",
  shortDescription: "Secure server dice, captures, safe squares, and two-to-four-player races.",
  longDescription:
    "An authoritative Ludo race with server-generated dice, configurable entry rules, local play, spectators, and reconnectable state.",
  category: "board",
  icon: "dice",
  cover: "ludo",
  minPlayers: 2,
  maxPlayers: 4,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "bubbles",
  stateSchema: ludoStateSchema,
  eventSchema: ludoEventSchema,
  settingsSchema: ludoSettingsSchema,
  createInitialState(context, settings) {
    const colors =
      context.playerIds.length === 2
        ? (["red", "yellow"] as const)
        : context.playerIds.length === 3
          ? (["red", "green", "yellow"] as const)
          : ludoColors;
    return {
      players: context.playerIds.slice(0, 4).map((id, index) => ({
        id,
        color: colors[index] ?? "red",
        tokens: [0, 1, 2, 3].map((tokenId) => ({ id: tokenId, progress: -1 })),
        finished: 0,
        captures: 0,
      })),
      turnIndex: 0,
      dice: null,
      phase: "roll",
      turnAvailableAt: null,
      actionDeadlineAt: context.now + settings.rollTimeSeconds * 1_000,
      rollNonce: 0,
      lastThrow: null,
      winnerIds: [],
      requireSixToLeave: settings.requireSixToLeave,
      requireCaptureBeforeHome: settings.requireCaptureBeforeHome,
      extraTurnOnSix: settings.extraTurnOnSix,
      extraTurnOnCapture: settings.extraTurnOnCapture,
      extraTurnOnHome: settings.extraTurnOnHome,
      threeSixesForfeit: settings.threeSixesForfeit,
      blockadesEnabled: settings.blockadesEnabled,
      fastMode: settings.fastMode,
      // The room server resolves the live platform control before play begins.
      // Direct/local engine usage remains uniform unless it explicitly opts in.
      smartDiceEnabled: false,
      rollTimeSeconds: settings.rollTimeSeconds,
      moveTimeSeconds: settings.moveTimeSeconds,
      consecutiveSixes: 0,
      sixChainSnapshot: null,
      history: [],
    };
  },
  validateEvent(state, event, context) {
    if (state.phase === "finished")
      return { valid: false, code: "finished", message: "The race is finished" };
    if (event.type === "set_fast_mode") return { valid: true };
    if (state.actionDeadlineAt !== null && context.now >= state.actionDeadlineAt)
      return {
        valid: false,
        code: "turn_expired",
        message: "Your timer expired. Topzle is completing the turn automatically.",
      };
    const player = state.players[state.turnIndex];
    if (!player || player.id !== context.actorId)
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    if (event.type === "roll")
      return {
        valid: state.phase === "roll",
        code: "already_rolled",
        message: "Move a token first",
      };
    if (state.phase !== "move" || state.dice === null)
      return { valid: false, code: "roll_first", message: "Roll before moving" };
    const token = player.tokens[event.tokenId];
    return token && canMoveLudoToken(state, player, token, state.dice)
      ? { valid: true }
      : { valid: false, code: "illegal_token", message: "That token cannot move" };
  },
  reduceEvent(state, event, context) {
    if (event.type === "set_fast_mode") return { ...state, fastMode: event.enabled };
    const player = state.players[state.turnIndex];
    if (!player) return state;
    if (event.type === "roll") {
      const rolled = rollLudoDice(state, context.randomInt ?? (() => 0));
      const dice = rolled.dice;
      const rollNonce = state.rollNonce + 1;
      const fallbackThrowStyle = {
        force: 0.48 + ((rollNonce * 17) % 35) / 100,
        momentumX: ((rollNonce % 3) - 1) * 18,
        momentumY: (((rollNonce + 1) % 3) - 1) * 14,
        pathTilt: ((rollNonce % 5) - 2) * 3,
      };
      const lastThrow = {
        playerId: player.id,
        rollNonce,
        ...(event.throwStyle ?? fallbackThrowStyle),
      };
      const rollEntry = {
        playerId: player.id,
        dice,
        tokenId: null,
        captureCount: 0,
        diceMode: rolled.profile.mode,
        diceWeights: rolled.profile.weights,
        at: context.now,
      };
      const movableTokens = player.tokens.filter((token) =>
        canMoveLudoToken(state, player, token, dice),
      );
      const hasMove = movableTokens.length > 0;
      const thirdConsecutiveSix =
        state.threeSixesForfeit && dice === 6 && state.consecutiveSixes === 2;
      if (thirdConsecutiveSix) {
        const baseline = state.sixChainSnapshot;
        const restoredPlayers = baseline
          ? clonePlayers(baseline.players)
          : clonePlayers(state.players);
        const restoredWinnerIds = baseline ? [...baseline.winnerIds] : [...state.winnerIds];
        const restoredState = {
          ...state,
          players: restoredPlayers,
          winnerIds: restoredWinnerIds,
        };
        const historyBeforeSixChain = baseline
          ? state.history.slice(0, baseline.historyLength)
          : state.history;
        return {
          ...state,
          players: restoredPlayers,
          winnerIds: restoredWinnerIds,
          dice,
          phase: "roll_result",
          turnAvailableAt:
            context.now + (state.fastMode ? LUDO_FAST_ROLL_RESULT_MS : LUDO_ROLL_RESULT_MS),
          actionDeadlineAt: null,
          rollNonce,
          lastThrow,
          consecutiveSixes: 0,
          sixChainSnapshot: null,
          turnIndex: nextTurn(restoredState),
          history: [...historyBeforeSixChain, rollEntry].slice(-500),
        };
      }
      const startsSixChain = state.threeSixesForfeit && dice === 6 && state.consecutiveSixes === 0;
      const sixChainSnapshot = startsSixChain
        ? {
            players: clonePlayers(state.players),
            winnerIds: [...state.winnerIds],
            historyLength: Math.min(state.history.length, 500),
          }
        : state.threeSixesForfeit && dice === 6
          ? state.sixChainSnapshot
          : null;
      const history = [...state.history, rollEntry];
      const activeSixChainSnapshot = hasMove ? sixChainSnapshot : null;
      return {
        ...state,
        dice,
        phase: hasMove ? "move" : "roll_result",
        turnAvailableAt: hasMove
          ? null
          : context.now + (state.fastMode ? LUDO_FAST_ROLL_RESULT_MS : LUDO_ROLL_RESULT_MS),
        actionDeadlineAt: hasMove
          ? context.now +
            (state.fastMode ? LUDO_FAST_DICE_PRESENTATION_MS : LUDO_DICE_PRESENTATION_MS) +
            (movableTokens.length === 1 ? 0 : state.moveTimeSeconds * 1_000)
          : null,
        rollNonce,
        lastThrow,
        turnIndex: hasMove ? state.turnIndex : nextTurn(state),
        consecutiveSixes:
          hasMove && state.threeSixesForfeit && dice === 6 ? state.consecutiveSixes + 1 : 0,
        sixChainSnapshot: activeSixChainSnapshot,
        history: activeSixChainSnapshot ? history : history.slice(-500),
      };
    }
    if (state.dice === null) return state;
    const players = clonePlayers(state.players);
    const movingPlayer = players[state.turnIndex];
    const token = movingPlayer?.tokens[event.tokenId];
    if (!movingPlayer || !token) return state;
    token.progress = token.progress === -1 ? 0 : token.progress + state.dice;
    const reachedHome = token.progress === 56;
    if (reachedHome) movingPlayer.finished += 1;
    const square = ludoGlobalSquare(movingPlayer.color, token.progress);
    let captureCount = 0;
    const capturedPlayerIds = new Set<string>();
    if (square !== null && !ludoSafeSquares.has(square)) {
      for (const opponent of players) {
        if (opponent.id === movingPlayer.id) continue;
        for (const opponentToken of opponent.tokens) {
          if (ludoGlobalSquare(opponent.color, opponentToken.progress) === square) {
            opponentToken.progress = -1;
            captureCount += 1;
            capturedPlayerIds.add(opponent.id);
          }
        }
      }
    }
    movingPlayer.captures += captureCount;
    const winnerIds = [...state.winnerIds];
    if (movingPlayer.finished === 4 && !winnerIds.includes(movingPlayer.id))
      winnerIds.push(movingPlayer.id);
    const lockedWinnerId = findTwoPlayerLockedWinner({
      players,
      requireCaptureBeforeHome: state.requireCaptureBeforeHome,
    });
    if (lockedWinnerId && !winnerIds.includes(lockedWinnerId)) winnerIds.push(lockedWinnerId);
    const remainingPlayers = players.filter(
      (candidate) => candidate.finished < 4 && !winnerIds.includes(candidate.id),
    );
    const finished = remainingPlayers.length <= 1;
    const extraTurn =
      movingPlayer.finished < 4 &&
      ((state.extraTurnOnSix && state.dice === 6) ||
        (state.extraTurnOnCapture && captureCount > 0) ||
        (state.extraTurnOnHome && reachedHome));
    const preservesSixChain =
      !finished &&
      extraTurn &&
      state.dice === 6 &&
      state.threeSixesForfeit &&
      state.sixChainSnapshot !== null;
    const history = [
      ...state.history,
      {
        playerId: movingPlayer.id,
        dice: state.dice,
        tokenId: event.tokenId,
        captureCount,
        capturedPlayerIds: [...capturedPlayerIds],
        reachedHome,
        at: context.now,
      },
    ];
    return {
      ...state,
      players,
      dice: null,
      phase: finished ? "finished" : "roll",
      turnAvailableAt: null,
      actionDeadlineAt: finished ? null : context.now + state.rollTimeSeconds * 1_000,
      winnerIds,
      turnIndex: finished || extraTurn ? state.turnIndex : nextTurn({ ...state, players }),
      consecutiveSixes: extraTurn && state.dice === 6 ? state.consecutiveSixes : 0,
      sixChainSnapshot: preservesSixChain ? state.sixChainSnapshot : null,
      history: preservesSixChain ? history : history.slice(-500),
    };
  },
  getPublicState: (state) => state,
  getPrivatePlayerState: () => ({}),
  getSpectatorState: (state) => state,
  getAvailableActions(state, playerId) {
    const player = state.players[state.turnIndex];
    if (!player || player.id !== playerId) return [];
    if (state.phase === "roll") return [{ type: "roll" }];
    if (state.phase !== "move") return [];
    return player.tokens.flatMap((token) =>
      state.dice !== null && canMoveLudoToken(state, player, token, state.dice)
        ? [{ type: "move", tokenId: token.id }]
        : [],
    );
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished") return null;
    return {
      status: "completed",
      winnerIds: state.winnerIds.slice(0, 1),
      placements: [
        ...state.winnerIds,
        ...state.players
          .filter((player) => !state.winnerIds.includes(player.id))
          .sort((left, right) => right.finished - left.finished)
          .map((player) => player.id),
      ],
      scores: Object.fromEntries(state.players.map((player) => [player.id, player.finished])),
      reason:
        state.players.find((player) => player.id === state.winnerIds[0])?.finished === 4
          ? "all_tokens_home"
          : "opponent_cannot_unlock_home",
    };
  },
  async createBotAction(state, botContext) {
    const policy = botContext.policy;
    return (["aggressive", "balanced", "defensive", "random"] as const).includes(
      policy as LudoResearchStrategy,
    )
      ? chooseLudoResearchAction(
          state,
          policy as LudoResearchStrategy,
          (maximum) => Math.abs(botContext.now + state.rollNonce * 17) % maximum,
        )
      : chooseLudoBotAction(state, botContext.difficulty);
  },
  getNextDeadline(state) {
    // Older snapshots and interrupted writes may be missing a deadline. Returning
    // zero asks the room alarm to repair the active phase immediately instead of
    // leaving the race permanently waiting.
    if (state.phase === "roll_result") return state.turnAvailableAt ?? 0;
    if (state.phase === "roll" || state.phase === "move") return state.actionDeadlineAt ?? 0;
    return null;
  },
  handleDeadline(state, context) {
    if (
      state.phase === "roll_result" &&
      (state.turnAvailableAt === null || state.turnAvailableAt <= context.now)
    )
      return {
        ...state,
        dice: null,
        phase: "roll",
        turnAvailableAt: null,
        actionDeadlineAt: context.now + state.rollTimeSeconds * 1_000,
      };
    if (
      (state.phase === "roll" || state.phase === "move") &&
      (state.actionDeadlineAt === null || state.actionDeadlineAt <= context.now)
    ) {
      if (state.phase === "roll")
        return markLatestLudoActionAutomatic(
          ludoGame.reduceEvent(state, { type: "roll" }, context),
        );
      if (state.phase === "move") {
        try {
          const player = state.players[state.turnIndex];
          const legalTokens =
            state.dice === null || !player
              ? []
              : player.tokens.filter((token) =>
                  canMoveLudoToken(state, player, token, state.dice ?? 0),
                );
          const soleLegalToken = legalTokens.length === 1 ? legalTokens[0] : undefined;
          const selected = soleLegalToken
            ? ({ type: "move", tokenId: soleLegalToken.id } as const)
            : chooseLudoBotAction(state, "medium");
          if (selected.type === "move")
            return markLatestLudoActionAutomatic(ludoGame.reduceEvent(state, selected, context));
        } catch {
          // Fall through to the invariant repair below.
        }
        // A move phase without a legal goti is malformed, but it must never trap
        // the room. Clear the roll and advance to the next unfinished player.
        return {
          ...state,
          turnIndex: nextTurn(state),
          dice: null,
          phase: "roll",
          turnAvailableAt: null,
          actionDeadlineAt: context.now + state.rollTimeSeconds * 1_000,
          consecutiveSixes: 0,
          sixChainSnapshot: null,
        };
      }
    }
    return state;
  },
  handlePlayerJoin(state, arrival) {
    if (
      state.phase === "finished" ||
      state.players.length >= 4 ||
      state.players.some((player) => player.id === arrival.playerId)
    )
      return null;
    const color = ludoColors.find(
      (candidate) => !state.players.some((player) => player.color === candidate),
    );
    if (!color) return null;
    const player: LudoState["players"][number] = {
      id: arrival.playerId,
      color,
      tokens: [0, 1, 2, 3].map((id) => ({ id, progress: -1 })),
      finished: 0,
      captures: 0,
    };
    return {
      state: {
        ...state,
        players: [...state.players, player],
        sixChainSnapshot: state.sixChainSnapshot
          ? {
              ...state.sixChainSnapshot,
              players: [...state.sixChainSnapshot.players, player],
            }
          : null,
      },
    };
  },
  handlePlayerDeparture(state, departure, context) {
    const removedIndex = state.players.findIndex((player) => player.id === departure.playerId);
    if (removedIndex < 0) return null;
    if (state.players.length === 2 && departure.replacementPlayerId) {
      const replacementPlayerId = departure.replacementPlayerId;
      const replacePlayer = (player: LudoState["players"][number]) =>
        player.id === departure.playerId ? { ...player, id: replacementPlayerId } : player;
      return {
        state: {
          ...state,
          players: state.players.map(replacePlayer),
          winnerIds: state.winnerIds.map((id) =>
            id === departure.playerId ? replacementPlayerId : id,
          ),
          sixChainSnapshot: state.sixChainSnapshot
            ? {
                ...state.sixChainSnapshot,
                players: state.sixChainSnapshot.players.map(replacePlayer),
                winnerIds: state.sixChainSnapshot.winnerIds.map((id) =>
                  id === departure.playerId ? replacementPlayerId : id,
                ),
              }
            : null,
        },
        replacementPlayerId,
      };
    }
    if (state.players.length <= 2) return null;
    const players = state.players.filter((player) => player.id !== departure.playerId);
    const winnerIds = state.winnerIds.filter((id) => id !== departure.playerId);
    const unfinishedPlayers = players.filter(
      (player) => player.finished < 4 && !winnerIds.includes(player.id),
    );
    const finished = unfinishedPlayers.length <= 1;
    const removedCurrentPlayer = removedIndex === state.turnIndex;
    const turnIndex = removedCurrentPlayer
      ? removedIndex % players.length
      : removedIndex < state.turnIndex
        ? state.turnIndex - 1
        : state.turnIndex;
    return {
      state: {
        ...state,
        players,
        turnIndex,
        dice: removedCurrentPlayer ? null : state.dice,
        phase: finished
          ? "finished"
          : removedCurrentPlayer && state.phase !== "finished"
            ? "roll"
            : state.phase,
        turnAvailableAt: removedCurrentPlayer ? null : state.turnAvailableAt,
        actionDeadlineAt: finished
          ? null
          : removedCurrentPlayer && state.phase !== "finished"
            ? context.now + state.rollTimeSeconds * 1_000
            : state.actionDeadlineAt,
        winnerIds,
        consecutiveSixes: removedCurrentPlayer ? 0 : state.consecutiveSixes,
        sixChainSnapshot: state.sixChainSnapshot
          ? {
              ...state.sixChainSnapshot,
              players: state.sixChainSnapshot.players.filter(
                (player) => player.id !== departure.playerId,
              ),
              winnerIds: state.sixChainSnapshot.winnerIds.filter((id) => id !== departure.playerId),
            }
          : null,
      },
    };
  },
};
